"use client";

import { isValidLocale, locales } from "@/i18n/locales";
import { instrumentation } from "@/instrumentation.config";
import { env } from "next-runtime-env";
import NextError from "next/error";
import { useEffect } from "react";

/**
 * Last-resort error page: it replaces the ROOT layout, so nothing from
 * `app/[locale]/layout.tsx` is mounted here — no `ObservabilityProvider`, no
 * `PublicEnvProvider`, no `PublicEnvScript`. Everything this component needs
 * has to come from a module import or degrade gracefully.
 *
 * That is why instrumentation is taken from the `@/instrumentation.config`
 * singleton instead of `useInstrumentation()`: the context has no provider
 * above it here, so the hook would always yield `null` and every root-layout
 * crash — the most severe failure this app has — would be silently dropped
 * instead of reported. `ObservabilityAgent` reads the same singleton.
 */
export default function GlobalError({
  error,
}: Readonly<{
  error: Error & { digest?: string };
}>) {
  useEffect(() => {
    instrumentation?.captureError(error);
  }, [error]);

  return (
    <html lang={resolveLangAttribute()} data-lean="true">
      <body>
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not expose status codes for errors, we simply pass 0 to render a
        generic error message. */}
        <NextError statusCode={0} />
      </body>
    </html>
  );
}

/**
 * Locale for the `lang` attribute, resolved defensively.
 *
 * Deliberately NOT `resolveDefaultLocale()`: that throws on a missing/invalid
 * env, which is exactly the misconfiguration most likely to have crashed the
 * root layout in the first place — and a `PublicEnvScript`-less tree means
 * `env()` returns undefined on the client regardless. Throwing here would take
 * down the error page itself and leave the user with Next's blank fallback.
 *
 * The fail-fast belongs to routing and the readiness probe (see
 * `src/i18n/locales.ts`); this attribute only needs a sane value, so it falls
 * back to the source-of-truth locale.
 */
function resolveLangAttribute(): string {
  const configured = env("NEXT_PUBLIC_DEFAULT_LOCALE");
  return isValidLocale(configured) ? configured : locales[0];
}
